import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  NucleusReferenceDataBucket,
  NucleusReferenceDataItem,
  NucleusReferenceDataItemFilter,
  NucleusReferenceDataItemReference,
  NucleusReferenceDataNavigatorContract,
  NucleusReferenceDataNotifierContract,
  NucleusReferenceDataPermissionsContract,
  NucleusReferenceDataServiceContract
} from '@nucleus/reference-data-ui';
import { Page, PageResult, ServiceHandler, BaseComponent } from '@nucleus/ui-base';
import { AuditTraceComponent } from '@nucleus/audit-report-ui';
import { ReferenceDataService } from '../../services/reference-data/reference-data.service';
import { PermissionCheckerService } from '../../services/permission/permission-checker.service';

/**
 * Thin adapters wiring TaskSense's ReferenceDataService + PermissionChecker +
 * BaseComponent's snackbar / confirm / report-download plumbing into the
 * @nucleus/reference-data-ui contracts. The lib never imports TaskSense code;
 * everything reaches it via DI. Mirror of goldfish's reference-data.adapter.ts.
 */

@Injectable({ providedIn: 'root' })
export class TasksenseReferenceDataServiceAdapter implements NucleusReferenceDataServiceContract {
  private svc: ReferenceDataService = inject(ReferenceDataService);

  findAllBuckets(handler: ServiceHandler<NucleusReferenceDataBucket[]>): void {
    this.svc.findAllBuckets(handler as any);
  }
  filter(bucket: string, handler: ServiceHandler<PageResult<NucleusReferenceDataItem>>, filter: NucleusReferenceDataItemFilter, page?: Page): void {
    this.svc.filter(bucket, handler as any, filter as any, page);
  }
  search(bucket: string, handler: ServiceHandler<NucleusReferenceDataItem[]>): void {
    this.svc.findAll(bucket, handler as any);
  }
  findAllReferences(bucket: string, handler: ServiceHandler<NucleusReferenceDataItemReference[]>): void {
    this.svc.findAllReferences(bucket, handler as any);
  }
  getById(bucket: string, id: number, handler: ServiceHandler<NucleusReferenceDataItem>): void {
    this.svc.getById(bucket, id, handler as any);
  }
  create(bucket: string, payload: NucleusReferenceDataItem, handler: ServiceHandler<NucleusReferenceDataItem>): void {
    this.svc.create(bucket, payload as any, handler as any);
  }
  update(bucket: string, id: number, payload: NucleusReferenceDataItem, handler: ServiceHandler<NucleusReferenceDataItem>): void {
    this.svc.update(bucket, id, payload as any, handler as any);
  }
  delete(bucket: string, id: number, handler: ServiceHandler<void>): void {
    this.svc.delete(bucket, id, handler as any);
  }
  getPDFReport(bucket: string, handler: ServiceHandler<Blob>, filter: NucleusReferenceDataItemFilter, page?: Page): void {
    this.svc.getPDFReport(bucket, handler, filter as any, page);
  }
  getXLSReport(bucket: string, handler: ServiceHandler<Blob>, filter: NucleusReferenceDataItemFilter, page?: Page): void {
    this.svc.getXLSReport(bucket, handler, filter as any, page);
  }
  getCSVReport(bucket: string, handler: ServiceHandler<Blob>, filter: NucleusReferenceDataItemFilter, page?: Page): void {
    this.svc.getCSVReport(bucket, handler, filter as any, page);
  }
}

@Injectable({ providedIn: 'root' })
export class TasksenseReferenceDataPermissionsAdapter implements NucleusReferenceDataPermissionsContract {
  private pc: PermissionCheckerService = inject(PermissionCheckerService);
  hasAccessToViewReferenceData():   boolean { return this.pc.hasAccessToViewReferenceData(); }
  hasAccessToCreateReferenceData(): boolean { return this.pc.hasAccessToCreateReferenceData(); }
  hasAccessToUpdateReferenceData(): boolean { return this.pc.hasAccessToUpdateReferenceData(); }
  hasAccessToDeleteReferenceData(): boolean { return this.pc.hasAccessToDeleteReferenceData(); }
  hasAccessToReferenceDataReport(): boolean { return this.pc.hasAccessToReferenceDataReport(); }
  hasAccessToAuditTrail():          boolean { return this.pc.hasAccessToAuditTrail(); }
  hasAccessToManageReferenceData(): boolean { return this.pc.hasAccessToManageReferenceData(); }
}

/** Bridges the lib's notifier contract into BaseComponent's snackbar +
 *  confirm + showReport plumbing. The audit-trail dialog is opened directly
 *  via MatDialog (AuditTraceComponent from @nucleus/audit-report-ui). */
@Injectable()
export class TasksenseReferenceDataNotifierAdapter
  extends BaseComponent
  implements NucleusReferenceDataNotifierContract {

  private matDialog: MatDialog = inject(MatDialog);

  success(message: string): void { super.showSuccess(message); }
  error(message: string):   void { super.showError(message); }
  override confirm(title: string, message: string): Promise<boolean> { return super.confirm(title, message); }
  accessDenied(): void { super.showAccessDeny(); }
  downloadReport(blob: Blob, filename: string, mimeType: string): void {
    super.showReport(blob, filename, mimeType);
  }
  openAuditTrail(entityName: string, entityId: string | number): void {
    this.matDialog.open(AuditTraceComponent, {
      panelClass: 'bw-dialog-panel',
      width: '1100px',
      maxWidth: '95vw',
      height: '72vh',
      data: { entityName, entityId }
    });
  }
}

/**
 * Bridges the lib's navigator contract onto TaskSense's routes. The global
 * bucket-browser drills into a bucket's item list (.../reference-data/<bucket>)
 * and the per-bucket list edits an item (.../<bucket>/<id ?? 'new'>).
 */
@Injectable({ providedIn: 'root' })
export class TasksenseReferenceDataNavigatorAdapter implements NucleusReferenceDataNavigatorContract {
  private router: Router = inject(Router);

  toList(bucket: string): void {
    this.router.navigate(['/administration/reference-data', bucket]);
  }
  toEdit(bucket: string, id: number | null): void {
    this.router.navigate(['/administration/reference-data', bucket, id ?? 'new']);
  }
}
